/**
 * <PdfPageCanvas> — render a single PDF page via pdf.js to a `<canvas>`.
 *
 * Standalone primitive. The canvas reports its natural aspect ratio (from
 * `page.getViewport`), so wrappers don't need to know the source PDF's
 * letter/A4/digest geometry up front — let `width: 100%; height: auto` carry
 * the height.
 *
 * Configure once at the entry point of the consuming app (Vite-friendly):
 *
 *   import { configurePdfJs } from '@okrapdf/page-review-ui/pdf-page-canvas';
 *   import workerSrc from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
 *   configurePdfJs({ workerSrc });
 *
 * `pdfjs-dist` is a peer dependency.
 */
import { Data, Effect } from 'effect';
import { useEffect, useRef, useState, type CSSProperties } from 'react';
// Peer dep — see package.json. The /* @vite-ignore */ avoids Vite trying to
// pre-bundle pdfjs-dist when this file is imported into a non-Vite consumer.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type PdfDocumentProxy = any;

let pdfJsPromise: Promise<typeof import('pdfjs-dist')> | null = null;
const docCache = new Map<string, Promise<PdfDocumentProxy>>();
let configuredWorkerSrc: string | null = null;

export class PageReviewPdfError extends Data.TaggedError('PageReviewPdfError')<{
  readonly stage: 'load' | 'render' | 'extract';
  readonly message: string;
}> {}

const pageReviewPdfCauses = new WeakMap<PageReviewPdfError, unknown>();

function pdfFailure(stage: PageReviewPdfError['stage'], cause: unknown): PageReviewPdfError {
  const error = new PageReviewPdfError({ stage, message: `Unable to ${stage} the PDF page.` });
  pageReviewPdfCauses.set(error, cause);
  return error;
}

function restorePdfError<A>(program: Effect.Effect<A, PageReviewPdfError>) {
  return program.pipe(
    Effect.catch((error) => Effect.fail(pageReviewPdfCauses.get(error) ?? error)),
  );
}

export function configurePdfJs(options: { workerSrc: string }): void {
  Effect.runSync(configurePdfJsEffect(options));
}

export function configurePdfJsEffect(options: { workerSrc: string }): Effect.Effect<void> {
  return Effect.sync(() => {
    configuredWorkerSrc = options.workerSrc;
  });
}

async function ensurePdfJs(): Promise<typeof import('pdfjs-dist')> {
  if (!pdfJsPromise) {
    pdfJsPromise = import('pdfjs-dist').then((mod) => {
      if (configuredWorkerSrc) {
        mod.GlobalWorkerOptions.workerSrc = configuredWorkerSrc;
      }
      return mod;
    });
  }
  return pdfJsPromise;
}

function loadPdfPromise(url: string): Promise<PdfDocumentProxy> {
  let promise = docCache.get(url);
  if (!promise) {
    promise = ensurePdfJs().then((pdf) => pdf.getDocument({ url, cMapPacked: true }).promise);
    docCache.set(url, promise);
  }
  return promise;
}

export function loadPdfEffect(url: string): Effect.Effect<PdfDocumentProxy, PageReviewPdfError> {
  return Effect.tryPromise({
    try: () => loadPdfPromise(url),
    catch: (cause) => pdfFailure('load', cause),
  });
}

export function loadPdf(url: string): Promise<PdfDocumentProxy> {
  return Effect.runPromise(restorePdfError(loadPdfEffect(url)));
}

// Cache of full-page raster canvases keyed by `${url}#${page}@${scale}` so a
// page renders once even when many regions crop from it.
const pageCanvasCache = new Map<string, Promise<HTMLCanvasElement>>();

async function renderPageCanvasPromise(
  url: string,
  pageNumber: number,
  scale: number,
): Promise<HTMLCanvasElement> {
  const key = `${url}#${pageNumber}@${scale}`;
  let cached = pageCanvasCache.get(key);
  if (!cached) {
    cached = (async () => {
      const doc = await loadPdfPromise(url);
      const page = await doc.getPage(pageNumber);
      const viewport = page.getViewport({ scale });
      const canvas = document.createElement('canvas');
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('canvas 2d context unavailable');
      await page.render({ canvasContext: ctx, viewport, canvas }).promise;
      return canvas;
    })();
    pageCanvasCache.set(key, cached);
  }
  return cached;
}

function renderPageCanvasEffect(
  url: string,
  pageNumber: number,
  scale: number,
): Effect.Effect<HTMLCanvasElement, PageReviewPdfError> {
  return Effect.tryPromise({
    try: () => renderPageCanvasPromise(url, pageNumber, scale),
    catch: (cause) => pdfFailure('render', cause),
  });
}

export interface ExtractPdfRegionOptions {
  /** Render scale for the source page. Larger = sharper crops. Default 2. */
  scale?: number;
  /** Padding (normalized 0-1) added around the bbox before cropping. Default 0. */
  padding?: number;
  /** Output mime type. Default `image/png`. */
  mimeType?: string;
  /** Quality for lossy encodings (0-1). Default undefined. */
  quality?: number;
}

/**
 * Render a normalized bbox region from a PDF page into a data URL.
 *
 * Renders the full page once (cached) then uses `drawImage` to crop the
 * region. Suitable for "extract every Picture block as <img src>" flows.
 */
export function extractPdfRegionEffect(
  url: string,
  pageNumber: number,
  bbox: { x: number; y: number; width?: number; height?: number; w?: number; h?: number },
  options: ExtractPdfRegionOptions = {},
): Effect.Effect<string, PageReviewPdfError> {
  return Effect.gen(function* () {
    const { scale = 2, padding = 0, mimeType = 'image/png', quality } = options;
    const w = bbox.width ?? bbox.w ?? 0;
    const h = bbox.height ?? bbox.h ?? 0;
    if (w <= 0 || h <= 0) {
      return yield* Effect.fail(
        pdfFailure('extract', new Error('bbox width/height must be positive')),
      );
    }

    const source = yield* renderPageCanvasEffect(url, pageNumber, scale);
    return yield* Effect.try({
      try: () => {
        const sx = Math.max(0, Math.floor((bbox.x - padding) * source.width));
        const sy = Math.max(0, Math.floor((bbox.y - padding) * source.height));
        const sw = Math.min(source.width - sx, Math.ceil((w + padding * 2) * source.width));
        const sh = Math.min(source.height - sy, Math.ceil((h + padding * 2) * source.height));

        const out = document.createElement('canvas');
        out.width = sw;
        out.height = sh;
        const ctx = out.getContext('2d');
        if (!ctx) throw new Error('canvas 2d context unavailable');
        ctx.drawImage(source, sx, sy, sw, sh, 0, 0, sw, sh);
        return out.toDataURL(mimeType, quality);
      },
      catch: (cause) => pdfFailure('extract', cause),
    });
  });
}

export function extractPdfRegion(
  url: string,
  pageNumber: number,
  bbox: { x: number; y: number; width?: number; height?: number; w?: number; h?: number },
  options: ExtractPdfRegionOptions = {},
): Promise<string> {
  return Effect.runPromise(restorePdfError(extractPdfRegionEffect(url, pageNumber, bbox, options)));
}

export interface PdfPageCanvasProps {
  /** URL to the PDF (any URL pdf.js can fetch — same-origin is simplest). */
  pdfUrl: string;
  /** 1-indexed page number. */
  pageNumber: number;
  /**
   * Logical scale multiplier for the rendered viewport. Combined with the
   * device pixel ratio for crisp retina output. Default 1.4.
   */
  scale?: number;
  className?: string;
  style?: CSSProperties;
  onError?: (err: Error) => void;
}

export function PdfPageCanvas({
  pdfUrl,
  pageNumber,
  scale = 1.4,
  className,
  style,
  onError,
}: PdfPageCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const doc = await loadPdf(pdfUrl);
        if (cancelled) return;
        const page = await doc.getPage(pageNumber);
        const canvas = canvasRef.current;
        if (!canvas) return;
        const dpr = Math.min(2, window.devicePixelRatio || 1);
        const viewport = page.getViewport({ scale: scale * dpr });
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        await page.render({ canvasContext: ctx, viewport, canvas }).promise;
      } catch (err) {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : String(err);
        setError(msg);
        onError?.(err instanceof Error ? err : new Error(msg));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [pdfUrl, pageNumber, scale, onError]);

  if (error) {
    return (
      <div className={className} style={{ ...style, padding: 8, fontSize: 12, color: '#991b1b' }}>
        page {pageNumber}: {error}
      </div>
    );
  }
  return (
    <canvas
      ref={canvasRef}
      className={className}
      style={{ display: 'block', width: '100%', height: 'auto', ...style }}
    />
  );
}
